import {
  Box,
  Typography,
  useTheme,
  Grid,
  Button,
} from "@mui/material";

import Header from "../../components/Header";
import { tokens } from "../../theme/tokens";

const Insights = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const Section = ({ children }) => (
    <Box
      my={6}
      p={4}
      backgroundColor="background.paper"
      borderRadius="8px"
    >
      {children}
    </Box>
  );

  const InsightCard = ({ title, description, status }) => (
    <Box
      p={3}
      height="100%"
      backgroundColor="background.default"
      borderRadius="8px"
      sx={{
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Typography
          variant="overline"
          sx={{ color: colors.houndGold?.[500] }}
        >
          {status}
        </Typography>

        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
      </Box>

      <Button
        variant="outlined"
        color="secondary"
        disabled
        sx={{ mt: 3, alignSelf: "flex-start" }}
      >
        Coming Soon
      </Button>
    </Box>
  );

  return (
    <Box>
      <Header
        title="INSIGHTS"
        subtitle="Practical notes on operations, clarity, systems, and the hidden friction inside growing businesses."
      />

      <Section>
        <Typography
          variant="h3"
          gutterBottom
          sx={{ color: colors.houndGold?.[500] }}
        >
          Thinking in progress.
        </Typography>

        <Typography variant="body1">
          This section will become a home for short, practical insights from
          the Operational Clarity project.
          <br />
          <br />
          The aim is not to publish generic business advice. The aim is to
          document real patterns, operational lessons, and useful ways of
          thinking that help growing businesses reduce confusion and work with
          more confidence.
        </Typography>
      </Section>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <InsightCard
            status="Draft Insight"
            title="Why Growing Businesses Lose Operational Clarity"
            description="A look at how complexity builds gradually as teams, processes, products, and responsibilities grow faster than visibility."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InsightCard
            status="Draft Insight"
            title="Processes That Live in People’s Heads"
            description="Why informal knowledge feels efficient early on, but becomes a bottleneck when teams grow or responsibilities shift."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InsightCard
            status="Draft Insight"
            title="The Hidden Cost of Workarounds"
            description="How small operational shortcuts become normalised, and why they often hide deeper process or ownership problems."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InsightCard
            status="Draft Insight"
            title="When Spreadsheets Become Operational Infrastructure"
            description="A practical reflection on the point where useful spreadsheets start carrying too much business-critical responsibility."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InsightCard
            status="Draft Insight"
            title="Clarity Before Software"
            description="Why better tools rarely fix unclear processes - and why understanding the work should come before choosing the system."
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <InsightCard
            status="Draft Insight"
            title="What Breweries Can Teach Us About Operations"
            description="Early notes on why independent breweries are a rich place to study production, logistics, hospitality, sales, and growth."
          />
        </Grid>
      </Grid>

      <Section>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: colors.houndGold?.[500] }}
        >
          Have an operational challenge worth exploring?
        </Typography>

        <Typography variant="body1" mb={3}>
          The best insights will come from real conversations with real
          businesses. If something in your operation feels unclear, repetitive,
          fragile, or harder than it should be, that may be worth exploring.
        </Typography>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          href="/contact"
        >
          Start a Conversation
        </Button>
      </Section>

      <Box mt={8} textAlign="center">
        <Typography variant="body2">
          Barry Marples - Operational Clarity
        </Typography>

        <Typography variant="caption" display="block" mt={2}>
          Clarity in operations • Alignment in teams • Confidence in decisions
        </Typography>
      </Box>
    </Box>
  );
};

export default Insights;